import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { Playbook, PlaybookStatus } from 'src/app/models/api/playbooks/playbook.model';
import { PermissionEnum } from 'src/app/models/auth0/permission.enum';
import { MenuAction } from 'src/app/models/menuAction.model';
import { PlaybooksHttpServiceService } from 'src/app/services/httpServices/playbooksHttpService/playbooks-http-service.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';

import { Label } from 'src/app/labels/englishLabels';
import { PlaybooksSupportedActions } from './playbooksSupportedActions';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { isBrownfieldTrigger } from 'src/app/common/playbookUtils';

type MenuItemDescriptor = [PlaybooksSupportedActions, PermissionEnum, string];

@Injectable({
  providedIn: 'root',
})
export class PlaybooksActionsService {
  private readonly _menuActions = [
    <MenuItemDescriptor>[PlaybooksSupportedActions.Edit, PermissionEnum.operator, 'common.edit'],
    <MenuItemDescriptor>[PlaybooksSupportedActions.View, PermissionEnum.viewer, 'commonActions.view'],
    <MenuItemDescriptor>[PlaybooksSupportedActions.Enable, PermissionEnum.operator, 'commonActions.enable'],
    <MenuItemDescriptor>[PlaybooksSupportedActions.Disable, PermissionEnum.operator, 'commonActions.disable'],
    <MenuItemDescriptor>[PlaybooksSupportedActions.Delete, PermissionEnum.operator, 'commonActions.delete'],
    <MenuItemDescriptor>[PlaybooksSupportedActions.Duplicate, PermissionEnum.operator, 'common.duplicate'],
  ].toMapByKeyValue(
    (descriptor) => descriptor[0],
    (descriptor) => this._createMenuAction(descriptor)
  );

  constructor(
    private _playbooksHttpService: PlaybooksHttpServiceService,
    private _permissionsService: PermissionsService,
    private _notificationService: NotificationsService,
    private _featureFlagsService: FeatureFlagsService,
    private _matDialog: MatDialog
  ) {}

  public getMenuActionForPlaybook(playbook: Playbook): MenuAction[] {
    const isOperator = this._permissionsService.userHasPermission(
      PERMISSIONS.PLAYBOOK_OPERATION,
      playbook.integration.id
    );
    const isPlaybookActive = playbook.status == PlaybookStatus.active;

    return [
      isOperator ? PlaybooksSupportedActions.Edit : PlaybooksSupportedActions.View,
      isPlaybookActive ? PlaybooksSupportedActions.Disable : PlaybooksSupportedActions.Enable,
      PlaybooksSupportedActions.Duplicate,
      PlaybooksSupportedActions.Delete,
    ].map((action) => this._menuActions.get(action) as MenuAction);
  }

  public handleActionClicked(action: PlaybooksSupportedActions, playbook: Playbook): Observable<string | undefined> {
    switch (action) {
      case PlaybooksSupportedActions.Enable:
        return this._updatePlaybookStatus(playbook, PlaybookStatus.active);

      case PlaybooksSupportedActions.Disable:
        return this._updatePlaybookStatus(playbook, PlaybookStatus.disabled);

      case PlaybooksSupportedActions.Delete:
        return this._deletePlaybook(playbook);
    }
    throw new Error(`PlaybooksActionsService handleActionClicked: ${action} support not implemented.`);
  }

  private _updatePlaybookStatus(playbook: Playbook, newStatus: PlaybookStatus): Observable<string | undefined> {
    const title = Label.get('playbooks.statusChangeTitle');
    const content =
      newStatus == PlaybookStatus.active
        ? Label.get('playbooks.enableStatusMessage')
        : Label.get('playbooks.disableStatusMessage');

    return this._openConfirmationDialog(title, content).pipe(
      mergeMap((confirmed) =>
        confirmed ? this._playbooksHttpService.playbookStatusUpdate(playbook.id, newStatus) : of(undefined)
      ),
      switchMap((confirmed) => {
        if (
          this._areBrownfieldPlaybooksEnabled() &&
          isBrownfieldTrigger(playbook.trigger.triggerType) &&
          newStatus == PlaybookStatus.active
        ) {
          return this._playbooksHttpService.triggerPlaybook(playbook.id).pipe(
            tap(() => this._notificationService.success('Playbook triggered')),
            map(() => confirmed)
          );
        }

        return of(confirmed);
      })
    );
  }

  private _deletePlaybook(playbook: Playbook): Observable<string | undefined> {
    const title = Label.get('playbooks.deleteTitle');
    const content = Label.get('playbooks.deleteMessage');

    return this._openConfirmationDialog(title, content).pipe(
      mergeMap((confirmed) => (confirmed ? this._playbooksHttpService.deletePlaybook(playbook.id) : of(undefined)))
    );
  }

  private _openConfirmationDialog(title: string, content: string): Observable<boolean> {
    const dialogRef = this._matDialog.open(ConfirmationDialogComponent, {
      data: {
        title: title,
        content: content,
      },
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
    });

    return dialogRef.afterClosed().pipe(map((userConfirmed?: boolean) => userConfirmed === true));
  }

  private _createMenuAction(menuItemDescriptor: MenuItemDescriptor): MenuAction {
    const hasPermissions = this._permissionsService.userHasPermission(PERMISSIONS.PLAYBOOK_OPERATION);
    return {
      name: menuItemDescriptor[0].toString(),
      displayNameLabel: menuItemDescriptor[2],
      disabled: !hasPermissions,
      tooltip: hasPermissions ? '' : Label.get('commonActions.adminActionTooltip'),
    } as MenuAction;
  }

  private _areBrownfieldPlaybooksEnabled(): boolean {
    return this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.BrownfieldPlaybooks);
  }
}
